/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext } from 'react';
import type { ShopperBasketsV2, ShopperOrders, ShopperProducts, ShopperPromotions } from '@/scapi';

/** Basket or Order – OrderSummary displays totals for both (e.g. cart and order details). */
export type OrderSummaryBasket = ShopperBasketsV2.schemas['Basket'] | ShopperOrders.schemas['Order'];

export interface OrderSummaryContextValue {
    basket: OrderSummaryBasket;
    isEstimate: boolean;
    i18nLanguage: string;
    currency: string;
    hasShippingPromos: boolean;
    shippingPromotionAdjustments: Array<{
        priceAdjustmentId?: string;
        itemText?: string;
        calloutMsg?: string;
        price?: number;
    }>;
    isZeroPay: boolean;
    taxPending: boolean;
    /**
     * True when the shopper's selected tender is Acima — either optimistically (radio just
     * clicked on the checkout page, before payment step submission) or already attached to
     * the basket. Consumed by `ServicesAndFees` to hide the sales-tax row (legacy SFRA
     * `ordertotals.isml` parity — Acima never leases tax, the shopper pays Acima directly
     * at lease e-sign) and by the root to subtract tax from the displayed total so the
     * math stays consistent.
     */
    hasAcimaPayment: boolean;
    /**
     * SSF-374 (row-split follow-up) — decomposed shipping-fee buckets that
     * mirror the JSON on `shippingLineItem.custom.priceDetails` (legacy
     * `org_ashcomm/.../calculate.js:768-780`; SFN parity in
     * `sfnext_ashley_hooks/ShippingCostHelper.writeNetPriceDetails`).
     *
     * Legacy `ordertotals.isml` renders three separate rows in the cart
     * summary — HD ("Delivery"), DS ("Shipping"), and the 3PBB flat WG fee
     * ("White Glove") — and SFN mirrors that by rendering three
     * item-presence-gated rows under a single "Shipping" section header
     * (labels updated to the design mockup: "White Glove (Ashley)" for HD,
     * "Shipping" for DS, "White Glove (3rd Party)" for the 3PBB fee).
     *
     * Each amount is the PRE-discount bucket net (as legacy has always
     * persisted). A separate ShippingPromoLine renders any promo discount
     * as its own row so the sum of visible rows still equals
     * `basket.shippingTotal` — no double-counting when a shipping promo
     * applies.
     *
     * Sourced from:
     *   - `whiteGloveTotal` — prefers `basket.c_whiteGloveTotal` (added by
     *     BasketEnrichmentHelper on every basket GET); falls back to
     *     `shippingItems[0].c_priceDetails.WHITEGLOVE.netPrice` for placed
     *     orders (which skip the basket hook).
     *   - `hdTotal` / `dsTotal` — parsed from
     *     `shippingItems[0].c_priceDetails.{HD,DS}.netPrice`.
     * `hdTotal` defaults to `0` when the bucket is absent (WG is never free
     * so $0 already renders as TBD via `renderWhiteGloveAmount`).
     * `dsTotal` is `null` when the DS bucket is absent (fee not yet computed
     * — no address entered) so the Ground Shipping row renders TBD instead
     * of FREE.  A present bucket with `netPrice: 0` means explicitly free.
     */
    hdTotal: number;
    dsTotal: number | null;
    whiteGloveTotal: number;
    /**
     * SSF-374 (row-split follow-up) — item-presence flags derived from
     * `c_deliveryMethod` on each product item (populated by
     * `OrderDeliveryMethodHelper` regardless of ZIP resolution). Drive row
     * VISIBILITY in the cart summary so the design's TBD state can show all
     * applicable rows even before fees are computed — a basket with HD +
     * DS3PL items shows both "White Glove (Ashley): TBD" and
     * "White Glove (3rd Party): TBD".
     *
     * Kept separate from the amount fields so post-ZIP the row can still
     * self-hide when the actual bucket fee is 0 (e.g. non-3PBB DS3PL basket
     * that failed the flat-fee predicate).
     */
    hasHdItems: boolean;
    hasDsItems: boolean;
    hasDs3plItems: boolean;
    displayTotal: number;
    /** INT-02 — merchandise subtotal AFTER subtracting any Extend warranty PLI
     *  cost. This is the value rendered in the "Subtotal" row so the customer
     *  sees a pure merchandise figure while the warranty cost shows in the
     *  dedicated "PROTECTION PLAN(S)" row below. Matches legacy SFRA
     *  `ordertotals.isml` (`adjSubTotal.subtract(pdict.totalPPCost)`). Falls
     *  back to `productSubTotal` when no warranty PLIs are in the basket. */
    merchandiseSubtotalDisplay: number;
    /** Promotions map (keyed by promotionId) from the Promotions API. Used to resolve calloutMsg for order-level and coupon-driven adjustments, since SCAPI does not echo calloutMsg back on basket price adjustment objects. */
    promotions: Record<string, ShopperPromotions.schemas['Promotion']>;
    /** Optional product details map keyed by productId for cart-page availability checks. */
    productsByItemIdForAvailability?: Record<string, ShopperProducts.schemas['Product']>;
    /**
     * SSF-373 — the shopper's current delivery-zip `zoneStatus` (mapped, unmapped,
     * unknown). Only `'unmapped'` gates the cart "Continue to Checkout" CTA per
     * Yasser's SSF-373 spec (Scenario 1, point 5). Optional: only the cart route
     * threads it in; other surfaces (order-detail, checkout order summary) leave
     * it undefined and the gate is a no-op.
     */
    deliveryZoneStatus?: 'mapped' | 'unmapped' | 'unknown';
    /**
     * SSF-373 follow-up — non-null when the shopper's active header ZIP falls in a
     * `disabledShippingZipCodePrefixes` entry (Guam / USVI). Hard-blocks the
     * "Continue to Checkout" CTA regardless of basket composition. Only the cart
     * route threads it in; other surfaces leave it undefined (gate is a no-op).
     *
     * Distinct from `deliveryZoneStatus === 'unmapped'`:
     *   - OOR (unmapped + HD in basket): shopper CAN fix by removing HD or
     *     changing ZIP to a mapped continental-US zone.
     *   - Territory-disabled: no ZIP in the disabled prefix range is shippable
     *     via ANY delivery method. Shopper must change ZIP or shop in-store.
     */
    disabledTerritory?: { prefix: string; territory: string } | null;
    /**
     * When `true`, the shipping row always displays "TBD" regardless of
     * `basket.shippingTotal`, and shipping is excluded from the displayed
     * total. Set on the cart page where the customer has not yet selected a
     * delivery method or entered a shipping address.
     */
    shippingTbd: boolean;
    /**
     * True when at least one merchandise PLI has `c_priceIsAvailable=false` —
     * stamped by `PricingAvailabilityHelper` on the cartridge when the
     * shopper's active zone price books can't cover the SKU. Consumed by
     * `Subtotal` and `Total` to render the shared `<UnavailablePrice>` label
     * instead of `formatCurrency(...)` (which would render a misleading
     * `$0.00` — the cartridge writes literal `0` on unpriced PLIs to protect
     * basket totals from `Money.NOT_AVAILABLE` poisoning). Conservative
     * "any PLI trips the whole summary" rule matches the checkout gate.
     */
    pricingUnavailable: boolean;
}

export const OrderSummaryContext = createContext<OrderSummaryContextValue | null>(null);

export function useOrderSummaryContext(): OrderSummaryContextValue {
    const ctx = useContext(OrderSummaryContext);
    if (!ctx) {
        throw new Error('useOrderSummaryContext must be used within an OrderSummary component');
    }
    return ctx;
}
